import { useEffect, useMemo, useRef, useState } from 'react';
import { baglantiTiklamasi } from './yonlendirme';
import { MenuIkon } from './menu-ikonlar';
import { SM } from './metin/solmenu';
import { tamSayi } from './metin/ortak';
import { hoverNiyeti, sabitDegistir, solMenuOku, solMenuYaz, type SolMenuDurumu } from './sol-menu-durum';
import { menuGruplari, type MenuMaddesi, type MenuOgesi } from './sol-menu-veri';
import { pazarlamaDurumu } from './pazarlama-durum';

export function SolMenu({ menu, izinler, yol, git }: {
  menu: readonly MenuMaddesi[]; izinler: readonly string[]; yol: string; git: (hedef: string) => void;
}) {
  const [durum, setDurum] = useState(() => solMenuOku(() => window.localStorage));
  const [gecici, setGecici] = useState(false);
  const [sekme, setSekme] = useState(() => pazarlamaDurumu().sekme);
  const [gruplar, setGruplar] = useState<Record<string, boolean>>({});
  const [fazla, setFazla] = useState(false);
  const aside = useRef<HTMLElement>(null);
  const pin = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const fazlaDugme = useRef<HTMLButtonElement>(null);
  const kilit = useRef(false);
  const fare = useRef(false);
  const fareOdagi = useRef(false);
  const niyet = useMemo(() => hoverNiyeti(window.setTimeout.bind(window), window.clearTimeout.bind(window)), []);
  const acik = durum.sabit || gecici;
  const veri = useMemo(() => menuGruplari(menu, izinler), [menu, izinler]);
  const etkin = (oge: MenuOgesi) => {
    if (oge.yol.includes('?sekme=')) return yol === '/pazarlama' && oge.yol.endsWith(`sekme=${sekme}`);
    return oge.yol === yol;
  };
  const etkinGrup = veri.find((g) => g.ogeler.some(etkin))?.ad;
  useEffect(() => {
    const oku = () => setSekme(pazarlamaDurumu().sekme);
    window.addEventListener('popstate', oku);
    window.addEventListener('crm:pazarlama', oku);
    return () => { window.removeEventListener('popstate', oku); window.removeEventListener('crm:pazarlama', oku); };
  }, []);
  useEffect(() => {
    if (etkinGrup) setGruplar((onceki) => ({ ...onceki, [etkinGrup]: true }));
  }, [etkinGrup, yol, sekme]);
  useEffect(() => () => niyet.iptal(), [niyet]);
  useEffect(() => {
    const kabuk = aside.current?.closest<HTMLElement>('.app-shell');
    const ust = kabuk?.querySelector<HTMLElement>('.topbar');
    if (!ust || !kabuk) return;
    const olc = () => kabuk.style.setProperty('--sm-ust', `${ust.offsetHeight}px`);
    olc();
    const gozlem = new ResizeObserver(olc);
    gozlem.observe(ust);
    return () => gozlem.disconnect();
  }, []);
  const kaydet = (d: SolMenuDurumu) => { setDurum(d); solMenuYaz(d, () => window.localStorage); };
  const geciciyiKapat = () => { if (!durum.sabit) setGecici(false); };
  const kapat = () => { niyet.iptal(); kilit.current = true; setGecici(false); };
  useEffect(() => {
    const escape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || durum.sabit || !acik || window.matchMedia('(max-width: 900px)').matches) return;
      if (document.querySelector('[popover]:popover-open, dialog[open]')) return;
      e.preventDefault();
      niyet.iptal(); kilit.current = true; setGecici(false);
      pin.current?.focus();
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [acik, durum.sabit, niyet]);
  const gezin = (hedef: string) => {
    niyet.iptal();
    if (!durum.sabit) { kilit.current = true; setGecici(false); }
    sheet.current?.hidePopover();
    const simdi = `${window.location.pathname}${window.location.search}`;
    if (simdi === hedef) return;
    // useYol aynı pathname için erken döner; bölüm başlığı sekme sorgusunu temizlemeli.
    if (window.location.pathname === hedef && window.location.search) {
      window.history.pushState(null, '', hedef);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    git(hedef);
  };
  /** Sayı tek rozettir: etikete yazılmaz, bağlantının adına da bir kez girer. */
  const rozet = (sayi?: number) => sayi ? <span className="sm-ek" data-testid="sm-rozet" title={SM.geciken(sayi)} aria-hidden="true">{tamSayi(sayi)}</span> : null;
  const baglanti = (oge: MenuOgesi, baslik = false, grupEtkin = false, sayiGoster = true) => <a key={oge.yol} href={oge.yol}
    className={`sm-link${baslik ? ' sm-baslik' : ''}`} data-ipucu={oge.etiket} aria-label={sayiGoster && oge.sayi ? `${oge.etiket}, ${SM.geciken(oge.sayi)}` : oge.etiket}
    aria-current={etkin(oge) ? 'page' : undefined} data-etkin={grupEtkin || undefined}
    onClick={baglantiTiklamasi(gezin, oge.yol)}>
    <MenuIkon ad={oge.ikon} /><span className="sm-etiket">{oge.etiket}{sayiGoster && rozet(oge.sayi)}</span>
  </a>;
  return <>
    <aside className="sm-sidebar" ref={aside} data-testid="sol-menu" data-acik={acik} data-sabit={durum.sabit}
      onPointerEnter={(e) => { if (e.pointerType === 'touch') return; fare.current = true; niyet.iptal(); /* enter, imlecin dışarıdan girdiği anlamına gelir: kilit (tıklama/Esc sonrası) yalnız imleç içerideyken odak yeniden açmasın diye */ kilit.current = false; niyet.gir(() => setGecici(true)); }}
      onPointerLeave={() => { fare.current = false; fareOdagi.current = false; kilit.current = false; niyet.cik(geciciyiKapat); }}
      onPointerDownCapture={() => { niyet.iptal(); fareOdagi.current = true; }}
      onPointerUpCapture={() => { fareOdagi.current = false; }}
      onPointerCancel={() => { fareOdagi.current = false; }}
      onFocusCapture={(e) => {
        if (fareOdagi.current || e.target === pin.current || (kilit.current && fare.current)) return;
        niyet.iptal(); setGecici(true);
      }}
      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) { niyet.cik(() => { if (!fare.current) geciciyiKapat(); }); } }}
      onKeyDown={(e) => { if (e.key === 'Escape' && !durum.sabit) { e.preventDefault(); kapat(); pin.current?.focus(); } }}>
      <nav id="sm-nav" aria-label={SM.gezinme} data-testid="primary-nav">
        {veri.map((g, i) => <div key={g.ad} data-testid={`sm-grup-${g.ad}`}>
          {i === 1 && <div className="sm-caption">{SM.alanlar}</div>}
          {g.ad === 'yonetim' && <div className="sm-caption">{SM.yonetim}</div>}
          {g.ad === 'yonetim' ? g.ogeler.map((o) => baglanti(o, true)) : <>
            <div className="sm-row">
              {baglanti(g.hedef, true, g.ad === etkinGrup, g.ogeler.length <= 1 || !gruplar[g.ad])}
              {i === 0 && <button ref={pin} type="button" className="sm-pin" data-testid="sol-menu-pin" aria-label={durum.sabit ? SM.birak : SM.sabitle} aria-pressed={durum.sabit}
          onClick={() => { niyet.iptal(); kilit.current = durum.sabit; setGecici(false); kaydet(sabitDegistir(durum)); if (durum.sabit) pin.current?.focus(); }}><MenuIkon ad="pin" /></button>}
              {g.ogeler.length > 1 && <button type="button" className="sm-ok" data-testid={`sm-ok-${g.ad}`} aria-label={`${g.etiket} ${SM.alt}`}
                aria-expanded={gruplar[g.ad] ?? false} aria-controls={`sm-liste-${g.ad}`} onClick={() => setGruplar((d) => ({ ...d, [g.ad]: !d[g.ad] }))}><MenuIkon ad="ok" /></button>}
            </div>
            {g.ogeler.length > 1 && <div className="sm-cocuklar" id={`sm-liste-${g.ad}`} data-testid={`sm-liste-${g.ad}`} data-acik={gruplar[g.ad] ? 'true' : 'false'} inert={!gruplar[g.ad]}><div className="sm-cocuklar-ic">{g.ogeler.map((o) => baglanti(o))}</div></div>}
          </>}
        </div>)}
      </nav>
    </aside>
    <nav className="sm-bottom" data-testid="alt-cubuk" aria-label={SM.mobil}>
      {veri.filter((g) => g.ad !== 'yonetim').map((g) => <a key={g.ad} href={g.hedef.yol} aria-label={g.hedef.sayi ? `${g.hedef.etiket}, ${SM.geciken(g.hedef.sayi)}` : g.hedef.etiket}
        aria-current={etkin(g.hedef) ? 'page' : undefined} data-etkin={g.ad === etkinGrup || undefined} onClick={baglantiTiklamasi(gezin, g.hedef.yol)}>
        <MenuIkon ad={g.hedef.ikon} /><span className="sm-bottom-label"><span className="sm-bottom-text">{g.ad === 'genel' ? SM.genel : g.baslik}</span>{rozet(g.hedef.sayi)}</span>
      </a>)}
      <button type="button" ref={fazlaDugme} data-testid="alt-cubuk-daha-fazla" popoverTarget="sm-more" aria-expanded={fazla} aria-controls="sm-more"><MenuIkon ad="fazla" /><span>{SM.fazla}</span></button>
    </nav>
    <div ref={sheet} id="sm-more" className="sm-sheet" popover="auto" role="dialog" aria-label={SM.fazla} data-testid="daha-fazla-liste"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); sheet.current?.hidePopover(); fazlaDugme.current?.focus(); } }}
      onToggle={(e) => { const acildi = e.currentTarget.matches(':popover-open'); setFazla(acildi); if (acildi) e.currentTarget.querySelector('button')?.focus(); }}>
      <div className="sm-sheet-head"><h2>{SM.fazla}</h2><button type="button" aria-label={SM.kapat} onClick={() => { sheet.current?.hidePopover(); fazlaDugme.current?.focus(); }}><MenuIkon ad="kapat" /></button></div>
      {veri.map((g) => {
        const kalan = g.ogeler.filter((o) => g.ad === 'yonetim' || o.yol !== g.hedef.yol);
        if (!kalan.length) return null;
        return <section key={g.ad}><h3 className="sm-caption">{g.etiket}</h3>{kalan.map((o) => baglanti(o, false, false, false))}</section>;
      })}
    </div>
  </>;
}
